import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { apiDelete, apiGet, apiPost, apiPut } from "components/Common/Axios";
import { Goal } from "lib/models";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "components/ui/button";
import { id } from "date-fns/locale";
import { Input } from "components/ui/input";
import { commaStringToNumber } from "lib/utils";

function SettingGoal() {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<Goal | undefined>(undefined);
  const [year, setYear] = useState<number>(2026);
  const [isDirty, setIsDirty] = useState(false);

  const loadGoals = async () => {
    const response = await apiGet("/goals");
    if (response.error || !response.data) {
      toast.error("목표 불러오는데 실패했습니다.");
      console.error("loadGoals error", response.error, response.data);
      return;
    }

    const newGoals = response.data;
    setGoals(newGoals);
    if (newGoals.length > 0) {
      setSelectedGoal(newGoals[0]);
    }
  };

  const updateGoal = async (goal: Goal) => {
    const response = await apiPut("/goal", goal);
    if (response.error || !response.data) {
      toast.error("목표 수정에 실패했습니다.");
      console.error("updateGoal error", response.error);
      return;
    }
    const updatedGoal = response.data;
    const newGoals = goals.map((g) =>
      g.id === updatedGoal.id ? updatedGoal : g
    );
    setGoals(newGoals);
    setSelectedGoal(updatedGoal);
    setIsDirty(false);
  };

  useEffect(() => {
    loadGoals();
  }, []);

  const handleAddGoal = async () => {
    const _goal = {
      id: 0,
      year: year,
      revenue: 0,
      profit: 0,
      count: 0,
    };

    const response = await apiPost("/goal", _goal);
    if (response.error) {
      toast.error("목표 추가에 실패했습니다.");
      console.error("handleAddGoal error", response.error);
      return;
    }

    const newGoal = response.data;

    setGoals([...goals, newGoal]);
    setSelectedGoal(_goal);
  };

  const handleDeleteGoal = async () => {
    if (!selectedGoal) return;

    const response = await apiDelete(`/goal/${selectedGoal.id}`);
    if (response.error || !response.data) {
      toast.error("목표 삭제에 실패했습니다.");
      console.error("handleDeleteGoal error", response.error, response.data);
      return;
    }

    const newGoals = goals.filter((goal) => goal.id !== selectedGoal.id);
    setGoals(newGoals);
    setSelectedGoal(newGoals[0]);
  };

  return (
    <div className="px-4 md:px-10 py-4 text-sm">
      <div className="pb-4">
        <Button variant="primary" onClick={() => handleAddGoal()}>
          추가
        </Button>
      </div>
      <div className="overflow-x-auto">
        <div className="grid grid-cols-[1fr_3fr_3fr_2fr_1fr] gap-x-10 min-w-[900px] max-w-[1200px] items-center gap-4 pb-2 text-center">
          <div>년도</div>
          <div>매출목표</div>
          <div>수익목표</div>
          <div>영업수</div>
        </div>
        {goals.map((goal) => (
          <div
            key={goal.id}
            className="grid grid-cols-[1fr_3fr_3fr_2fr_1fr] gap-x-10 min-w-[900px] max-w-[1200px] items-center gap-4 pb-4"
          >
            <div className="flex items-center gap-2 w-full">
              <Input
                value={goal.year}
                onChange={(e) => {
                  const newGoal = { ...goal, year: parseInt(e.target.value) };
                  setGoals(goals.map((g) => (g.id === goal.id ? newGoal : g)));
                  setSelectedGoal(newGoal);
                  setIsDirty(true);
                }}
                onBlur={async () => {
                  await updateGoal(goal);
                }}
                className="flex-1 text-center"
              />{" "}
              <span>년</span>
            </div>
            <div className="flex items-center gap-2 w-full">
              <Input
                value={goal.revenue.toLocaleString()}
                onChange={(e) => {
                  const newGoal = {
                    ...goal,
                    revenue: commaStringToNumber(e.target.value),
                  };
                  setGoals(goals.map((g) => (g.id === goal.id ? newGoal : g)));
                  setSelectedGoal(newGoal);
                  setIsDirty(true);
                }}
                onBlur={async () => {
                  await updateGoal(goal);
                }}
                className="flex-1 text-right"
              />{" "}
              <span>원</span>
            </div>
            <div className="flex items-center gap-2 w-full">
              <Input
                value={goal.profit.toLocaleString()}
                onChange={(e) => {
                  const newGoal = {
                    ...goal,
                    profit: commaStringToNumber(e.target.value),
                  };
                  setGoals(goals.map((g) => (g.id === goal.id ? newGoal : g)));
                  setSelectedGoal(newGoal);
                  setIsDirty(true);
                }}
                onBlur={async () => {
                  await updateGoal(goal);
                }}
                className="flex-1 text-right"
              />{" "}
              <span>원</span>
            </div>
            <div className="flex items-center gap-2 w-full">
              <Input
                value={goal.count.toLocaleString()}
                onChange={(e) => {
                  const newGoal = {
                    ...goal,
                    count: commaStringToNumber(e.target.value),
                  };
                  setGoals(goals.map((g) => (g.id === goal.id ? newGoal : g)));
                  setSelectedGoal(newGoal);
                  setIsDirty(true);
                }}
                onBlur={async () => {
                  await updateGoal(goal);
                }}
                className="flex-1 text-right"
              />{" "}
              <span>개</span>
            </div>
            <Button
              variant="destructive"
              size="md"
              onClick={() => handleDeleteGoal()}
            >
              삭제
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SettingGoal;
